(() => {
  if (window.__paragonRiseSwitcher) return;
  window.__paragonRiseSwitcher = true;

  const getSelectedSize = (section) => {
    for (const input of section.querySelectorAll('variant-picker input[type="radio"]:checked')) {
      const fieldset = input.closest('fieldset');
      const legend = fieldset?.querySelector('legend')?.textContent?.trim().toLowerCase() || '';
      const name = input.getAttribute('name')?.toLowerCase() || '';
      if (legend.startsWith('size') || name.startsWith('size-')) return input.value;
    }

    for (const select of section.querySelectorAll('variant-picker select')) {
      const label = section.querySelector(`label[for="${select.id}"]`)?.textContent?.trim().toLowerCase() || '';
      if (label.startsWith('size')) return select.value;
    }

    return '';
  };

  const getVariantIdForSize = async (productUrl, selectedSize) => {
    if (!selectedSize) return '';

    const jsonUrl = new URL(productUrl);
    jsonUrl.pathname = `${jsonUrl.pathname.replace(/\/$/, '')}.js`;
    jsonUrl.search = '';

    const response = await fetch(jsonUrl, { headers: { Accept: 'application/json' } });
    if (!response.ok) return '';

    const product = await response.json();
    const sizeIndex = (product.options || []).findIndex((option) => {
      const name = typeof option === 'string' ? option : option?.name;
      return String(name || '').trim().toLowerCase().startsWith('size');
    });
    const variant = (product.variants || []).find((candidate) => {
      const value = sizeIndex >= 0 ? candidate.options?.[sizeIndex] : '';
      return value === selectedSize;
    });

    return variant?.id ? String(variant.id) : '';
  };

  // Window capture runs before document-level landing-page integrations, which
  // can otherwise consume the link click and navigate to the PDP.
  window.addEventListener(
    'click',
    async (event) => {
      const link = event.target.closest('rise-switcher a');
      if (!link || link.getAttribute('aria-current') === 'page') return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const switcher = link.closest('rise-switcher');
      const currentSection = switcher?.closest(
        'featured-product-information, .featured-product-section, [data-testid^="featured-product-"]'
      );
      // Any Rise selector inside an embedded Featured Product must stay on
      // that landing page. PDP rise selectors have no such section ancestor.
      const morphInPlace = switcher?.dataset.morphInPlace === 'true' || Boolean(currentSection);
      const sectionId = switcher?.dataset.sectionId || '';

      event.preventDefault();
      event.stopImmediatePropagation();

      if (link.dataset.loading === 'true') return;
      link.dataset.loading = 'true';
      switcher.setAttribute('aria-busy', 'true');

      try {
        const productUrl = new URL(link.href, window.location.origin);
        const selectedSize = currentSection ? getSelectedSize(currentSection) : '';
        const variantId = await getVariantIdForSize(productUrl, selectedSize);
        if (variantId) productUrl.searchParams.set('variant', variantId);

        if (!morphInPlace || !sectionId) {
          window.location.assign(productUrl.toString());
          return;
        }

        if (!currentSection) throw new Error('In-place target section not found');

        const sectionUrl = new URL(productUrl);
        sectionUrl.searchParams.set('section_id', sectionId);
        const response = await fetch(sectionUrl, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`Section request failed: ${response.status}`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextSection = currentSection.matches('featured-product-information')
          ? html.querySelector('featured-product-information')
          : html.querySelector('.featured-product-section');
        if (!nextSection) throw new Error('Featured product section not found');

        const selectedRise = link.textContent.trim();
        const nextSwitcher = nextSection.querySelector('rise-switcher');
        if (nextSwitcher) {
          nextSwitcher.dataset.currentRise = selectedRise;
          const nextLabel = nextSwitcher.querySelector('.rise-switcher__label');
          if (nextLabel) nextLabel.textContent = selectedRise;

          nextSwitcher.querySelectorAll('a').forEach((optionLink) => {
            const optionUrl = new URL(optionLink.href, window.location.origin);
            const isSelected = optionUrl.pathname === productUrl.pathname;
            optionLink.toggleAttribute('aria-current', isSelected);
            optionLink.querySelector('.rise-switcher__option')?.classList.toggle('rise-switcher__option--active', isSelected);
          });
        }

        currentSection.replaceWith(nextSection);
      } catch (error) {
        console.warn('[rise-switcher] product switch failed', error);
        if (!morphInPlace || !sectionId) {
          window.location.assign(link.href);
        }
      } finally {
        link.dataset.loading = 'false';
        switcher.removeAttribute('aria-busy');
      }
    },
    true
  );
})();
